import React, { createContext, useState, useContext, useEffect } from "react";

const OptionRecordContext = createContext();

function OptionRecordProvider({ children }) {
    const STORAGE_KEY = 'optionRecords';
    const [optionRecords, setOptionRecords] = useState([]);

    // Load data from sessionStorage on initial render
    useEffect(() => {
        const savedData = sessionStorage.getItem(STORAGE_KEY);
        if (savedData) {
            try {
                const parsedData = JSON.parse(savedData);
                if (Array.isArray(parsedData)) {
                    setOptionRecords(parsedData);
                }
                
            } catch (error) {
                console.error('Failed to parse sessionStorage data', error);
                sessionStorage.removeItem(STORAGE_KEY);
            }
        }
    }, []);

    // Save data to sessionStorage whenever it changes
    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(optionRecords));
    }, [optionRecords]);

    // Helper function to validate question_id
    const isValidQuestionId = (id) => {
        return id !== undefined && id !== null && !isNaN(Number(id));
    };

    // Add a new option record or update existing one
    const addOptionRecord = (newRecord) => {
        if (!isValidQuestionId(newRecord?.question_id)) {
            console.warn('Invalid question_id - record not saved');
            return;
        }

        setOptionRecords(prevRecords => {
            const questionId = Number(newRecord.question_id);
            const existingIndex = prevRecords.findIndex(
                record => Number(record.question_id) === questionId
            );

            let updatedRecords;
            if (existingIndex >= 0) {
                // Update existing record
                updatedRecords = [...prevRecords];
                updatedRecords[existingIndex] = {
                    ...updatedRecords[existingIndex],
                    ...newRecord,
                    question_id: questionId
                };
            } else {
                // Add new record
                updatedRecords = [
                    ...prevRecords,
                    {
                        ...newRecord,
                        question_id: questionId
                    }
                ];
            }
            
            return updatedRecords;
        });
    };

    // Get all option records
    const getOptionRecords = () => {
        return optionRecords;
    };

    // Get option record by question ID
    const getOptionRecordById = (questionId) => {
        if (!isValidQuestionId(questionId)) return null;
        return optionRecords.find(
            record => Number(record.question_id) === Number(questionId)
        );
    };

    // Update specific fields of an option record
    const updateOptionRecord = (questionId, updatedFields) => {
        if (!isValidQuestionId(questionId)) {
            console.warn('Invalid question_id - update failed');
            return;
        }

        setOptionRecords(prevRecords =>
            prevRecords.map(record =>
                Number(record.question_id) === Number(questionId)
                    ? { ...record, ...updatedFields }
                    : record
            )
        );
    };

    // Delete an option record by question ID
    const deleteOptionRecord = (questionId) => {
        if (!isValidQuestionId(questionId)) {
            console.warn('Invalid question_id - delete failed');
            return;
        }

        setOptionRecords(prevRecords =>
            prevRecords.filter(
                record => Number(record.question_id) !== Number(questionId)
            )
        );
    };

    // Clear all option records (from both state and sessionStorage)
    const clearOptionRecords = () => {
        setOptionRecords([]);
        sessionStorage.removeItem(STORAGE_KEY);
    };

    const value = {
        optionRecords,
        addOptionRecord,
        getOptionRecords,
        getOptionRecordById,
        updateOptionRecord,
        deleteOptionRecord,
        clearOptionRecords
    };

    return (
        <OptionRecordContext.Provider value={value}>
            {children}
        </OptionRecordContext.Provider>
    );
}

const useOptionRecord = () => {
    const context = useContext(OptionRecordContext);
    if (context === undefined) {
        throw new Error('useOptionRecord must be used within a OptionRecordProvider');
    }
    return context;
};

export { useOptionRecord };
export default OptionRecordProvider;